/**
 * useSwUpdate hook (Plan 01-15, Task 1).
 *
 * Bridges Plan 01-08's `registerSW` lifecycle callback into the Plan 01-15
 * `SwUpdateStore`. Mount once at the React root (App.tsx) — a module-level
 * `armed` guard makes additional mounts/remounts a no-op so the underlying
 * `navigator.serviceWorker.register('/sw.js')` is requested at most once.
 *
 * Plan 01-08's `main.tsx` already calls `registerSW()` BARE so the
 * `controllerchange` reload guard is wired before any React mounts. That call
 * armed the Plan 08 module-level `registered` flag, so `registerSW(...)` here
 * becomes a no-op for the registration itself. The callbacks we pass via
 * this second invocation will not be wired in that scenario — which means
 * THIS plan owns the canonical registration path.
 *
 * Resolution: Task 1 also removes the bare `registerSW()` from main.tsx and
 * relies on App.tsx → useSwUpdate() as the single registration call.
 */
import { useEffect } from 'react';
import { registerSW } from '../../lib/sw-register';
import { useSwUpdateStore } from './SwUpdateStore';

let armed = false;

export function useSwUpdate(): void {
  useEffect(() => {
    if (armed) return;
    armed = true;
    const setUpdate = useSwUpdateStore.getState().setUpdate;
    registerSW({
      onNeedRefresh: (reg) => setUpdate(reg),
    });
  }, []);
}

/** Test-only — resets the module-level guard. Do not use in app code. */
export function __resetArmedForTests(): void {
  armed = false;
}
